import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
// routes
import { paths } from 'src/routes/paths';
import { useRouter, useParams } from 'src/routes/hook';
// assets
import { countries } from 'src/assets/data';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { enqueueSnackbar, useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFTextField,
  RHFAutocomplete,
  RHFSelect,
} from 'src/components/hook-form';
import { endpoints, getFetcher1, getFetcherPrams, postFetcher, postFetcher1, postPramsFetcher1, putFetcher1 } from 'src/utils/axios';
import 'react-phone-number-input/style.css';
import TourGuideConfigureSchemaListUI from './tour-guide-configure-schema-list-ui';

// ----------------------------------------------------------------------
// Form validation schema
const TourGuideSchema = Yup.object().shape({
  configurationName: Yup.string().required('Configuration Name is required'),
  headerId: Yup.object()
    .nullable()
    .required('Schema is required'),
  type: Yup.number()
    .oneOf([1, 2], 'Type is required')
    .required('Type is required'),
  // Validate categoryLevel and category only if type is 1
  categoryLevel: Yup.number()
    .when('type', {
      is: 1,
      then: (schema) =>
        schema
          .oneOf([0, 1, 2], 'Category Level is required')
          .required('Category Level is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  categoryId: Yup.object()
    .nullable()
    .when('type', {
      is: 1,
      then: (schema) => schema.required('Category is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  discountMethod: Yup.number()
    .oneOf([1, 2], 'Discount Method is required')
    .required('Discount Method is required'),
  amount: Yup.number()
    .typeError('Amount must be a number')
    .min(0, 'Amount must be at least 0')
    .required('Amount is required'),
});
export default function TourGuideConfigureEditForm({ currentUser, isEditMode }) {



  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [schemaList, setSchemaList] = useState([]);
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [schemaName, setSchemaName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [categoryLevel, setCategoryLevel] = useState(0);
  const [toolbarSchemaId, setToolbarSchemaId] = useState(0);
  const [search, setSearch] = useState("");

  console.log('tableData', tableData);

  // Default values for the form
  const defaultValues = useMemo(
    () => ({
      type: 1,
      discountMethod: 1,
      isEnabled,
      categoryLevel: 0,
      // isEnabled,
      // categoryLevel: currentUser?.categoryLevel ?? 0,
    }),
    [isEnabled]
  );

  // React Hook Form methods
  const methods = useForm({
    resolver: yupResolver(TourGuideSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Simulate loading state
  const loadingSend = useBoolean(false);
  // fetch api
  const fetchLines = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      setTableLoading(true);
      const param = {
        name: search === null ? '' : search || '',
        schemaId: toolbarSchemaId?.id || 0,
      };
      const response = await postPramsFetcher1(`${endpoints.tourGuide.createTourGuideLine}/${page}/${size}/${0}`, null, param, config);
      if (!response || (response.status !== 201 && response.status !== 200)) {
        setTableData(null);

        throw new Error(response?.message || 'Failed to fetch lines');
      }
      console.log('Response from fetchLines:', response);

      setTableData(response?.data);

    } catch (error) {
      setTableData(null);
      console.error('Error fetching schemas:', error);
    }
    setTableLoading(false);
    setLoading(false);


  }, [search, toolbarSchemaId, page, size]);


  // create api call
  const handleCreateAndSend = handleSubmit(async (data) => {
    console.log('handleCreateAndSend called with data:', data);
    loadingSend.onTrue();
    const payload = {
      id: updateId || null,
      configurationName: data.configurationName,
      headerId: data.headerId?.id,
      type: data.type,
      discountMethod: data.discountMethod,
      amount: data.amount,
      categoryLevel: data.categoryLevel,
      categoryId: data.categoryId?.id,
      isEnabled,
    };
    console.log('Payload for API call:', payload);

    try {
      const response = (updateId) === null ? await postFetcher1(endpoints.tourGuide.createTourGuideLine, payload) : await putFetcher1(`${endpoints.tourGuide.createTourGuideLine}/${updateId}`, payload);
      await fetchLines();
      if (!response || (response.status !== 201 && response.status !== 200)) {
        enqueueSnackbar('Failed to create tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
        setIsEnabled(true);
        throw new Error(response?.message || 'Failed to create tour guide schema');

      } else {


        enqueueSnackbar('Tour guide schema created successfully', {
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
        // Reset form if needed
        methods.reset({
          id: updateId || null,
          configurationName: '',
          headerId: null,
          type: 1,
          discountMethod: 1,
          amount: '',
          categoryLevel: 0,
          categoryId: null,
          isEnabled,
        });

        methods.reset();
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Show success message or handle response
    } catch (error) {
      enqueueSnackbar('Failed to create tour guide schema', {
        variant: 'error',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        sx: {
          width: '400px',
        },
      });
    } finally {
      loadingSend.onFalse();
    }
    setUpdateId(null); // Reset updateId after submission


  });


  const setToolbarSchemaIdFromChild = (id, name) => {
    console.log('setToolbarSchemaIdFromChild called with id:', id, ' name:', name);
    setToolbarSchemaId(id);
    setSearch(name);
  };


  const fetchTourGuideSchemas = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const param = {
        name: schemaName || '',
      };
      const response = await postPramsFetcher1(`${endpoints.tourGuide.search}/${0}/${10}/${0}`, null, param, config);

      console.log('Response from fetchTourGuideSchemas:', response?.data?.content);

      if (response && response.status === 200) {
        setSchemaList(response?.data?.content || []);
      } else {
        enqueueSnackbar('Failed to fetch tour guide schemas', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: { width: '400px' },
        });
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch tour guide schemas', {
        variant: 'error',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
    setLoading(false);

  }, [schemaName]);

  const handleEditRowEnable = useCallback(
    async (row, isEnabled2) => {
      setLoading(true);
      console.log('rowFire', row);
      let isEnabled1;
      if (isEnabled2 === true || isEnabled2 === 'true' || isEnabled2 === '1') {
        isEnabled1 = false;
      }
      else {
        isEnabled1 = true;
      }

      try {
        const payload = {
          ...row,
          isEnabled: isEnabled1,
        };
        const response = await putFetcher1(`${endpoints.tourGuide.createTourGuideLine}/${row.id}`, payload);
        if (!response || response.status !== 200) {
          enqueueSnackbar('Failed to update tour guide schema', {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { width: '400px' },
          });
        } else {
          enqueueSnackbar('Tour guide schema updated successfully', {
            variant: 'success',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { width: '400px' },
          });

          fetchTourGuideSchemas();
        }
      } catch (error) {
        enqueueSnackbar('Failed to update tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: { width: '400px' },
        });
      }

      await fetchLines();
    },
    [fetchTourGuideSchemas, fetchLines]
  );


  const setNameFromChild = (name) => {
    // setName(name);
  };
  useEffect(() => {
    function fetchTourGuideSchemas1() {
      fetchTourGuideSchemas();
    }
    fetchTourGuideSchemas1();
  }, [fetchTourGuideSchemas]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handler to set the page size
  const handleSizeChange = (newSize) => {
    setSize(newSize);
    // setPage(0); // Optionally reset to first page when size changes
  };


  const handleEditRow = useCallback(
    async (row) => {

      try {
        // Push row?.tourGuideSchema to schemaList if not already present (by id)
        if (row?.tourGuideSchema && !schemaList.some(s => s.id === row.tourGuideSchema.id)) {
          setSchemaList(prev => [...prev, row.tourGuideSchema]);
        }

      } catch (error) {
        console.log('Error adding category:', error);
      }
      try {
        if (row?.category && !category.some(c => c.id === row.category.id)) {
          setCategory(prev => [...prev, row.category]);
        }
      } catch (error) {
        console.log('Error adding category:', error);
      }
      // If row?.category exists and not already in category, add it


      console.log('Editing row:', row);
      methods.setValue('type', row?.type || 1);
      methods.setValue('amount', row?.amount || '');
      methods.setValue('headerId', row?.tourGuideSchema || null);
      methods.setValue('categoryId', row?.category || null);
      methods.setValue('categoryLevel', row?.categoryLevel || 0);
      methods.setValue('categoryName', row?.categoryName || '');
      methods.setValue('configurationName', row?.configurationName || '');
      methods.setValue('discountMethod', row?.discountMethod || 1);
      setIsEnabled(row?.isEnabled);
      setCategoryLevel(row?.categoryLevel || 0);


      setUpdateId(row?.id || null);
      if (row?.type === 1) {
        setCategoryLevel(row?.categoryLevel || 0);
      }
    },
    [methods, schemaList, category]
  );


  useEffect(() => {
    // categoryLevel

    const fetchCategories = async () => {
      // categoryName
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      setLoading(true);
      const param = {
        name: categoryName || '',
      };

      const response = await postPramsFetcher1(`${endpoints.category.categoryLevel}/${0}/${10}/${0}/${categoryLevel}`, null, param, config);
      if (!response || (response.status !== 201 && response.status !== 200)) {
        throw new Error(response?.message || 'Failed to create tour guide schema');
      }
      if (Array.isArray(response?.data?.content)) {
        setCategory(response?.data?.content);
      } else {
        setCategory([]);
      }
      setLoading(false);

    }
    fetchCategories();
  }, [categoryLevel, categoryName]);

  console.log('Fetching schemas with page:', page, 'size:', size);


  useEffect(() => {

    fetchLines();

  }, [page, size, toolbarSchemaId, search, fetchLines]);

  return (
    <FormProvider methods={methods}>
      <Grid container spacing={3}>
        <Grid xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Schema Configure Details</Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
              sx={{ mb: 2 }}
            >
              <RHFTextField name="configurationName" label="Configuration Name" size="small" value={methods.watch('configurationName') || ''} />
              <RHFAutocomplete
                name="headerId"
                label="Schema"
                size="small"
                options={schemaList}
                value={methods.watch('headerId') || null}
                getOptionLabel={(option) => option.name || ''}
                onInputChange={(_, value) => {
                  setSchemaName(value);
                }}
                onChange={(_, value) => {
                  methods.setValue('headerId', value || null);
                }}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option?.name || '-'} - {option?.code || '-'}
                  </li>
                )}
              />

              <RHFSelect name="type" label="Type" size="small"
                value={methods.watch('type') ?? 1}

              >
                <MenuItem value={1}>Category</MenuItem>
                <MenuItem value={2}>Item</MenuItem>
              </RHFSelect>

              {/* Show Category Level only if type is 1 */}
              {methods.watch('type') === 1 && (
                <RHFSelect
                  name="categoryLevel"
                  label="Category Level"
                  size="small"
                  value={methods.watch('categoryLevel') ?? 0}
                  onChange={e => {
                    const value = Number(e.target.value);
                    methods.setValue('categoryLevel', value);
                    setCategoryLevel(value);
                    console.log('Category Level changed:', value);
                  }}
                >
                  <MenuItem value={0}>Level 1 Category</MenuItem>
                  <MenuItem value={1}>Level 2 Category</MenuItem>
                  <MenuItem value={2}>Level 3 Category</MenuItem>
                </RHFSelect>
              )}

              {methods.watch('type') === 1 && (
                <RHFAutocomplete
                  name="categoryId"
                  label="Category"
                  size="small"
                  options={category}
                  value={methods.watch('categoryId') || ''}
                  getOptionLabel={(option) => option.category_name || ""}
                  onInputChange={(_, value) => {
                    setCategoryName(value);
                  }}
                  onChange={(_, value) => {
                    methods.setValue('categoryId', value || null);
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option?.category_name || '-'}
                    </li>
                  )}
                />
              )}


              <RHFSelect name="discountMethod" label="Discount Method" size="small"
                value={methods.watch('discountMethod') ?? 1}>
                <MenuItem value={1}>Percentage</MenuItem>
                <MenuItem value={2}>Amount</MenuItem>
              </RHFSelect>



              <RHFTextField
                name="amount"
                label="Amount"
                type="number"
                value={methods.watch('amount') || ''}
                inputProps={{ min: 0, step: "any" }}
                size="small"
              />

            </Box>
            <Stack justifyContent="flex-end" direction="row" spacing={2} sx={{ mt: 2 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loadingSend.value && isSubmitting}
                onClick={handleCreateAndSend}
                size="small"
              >
                {updateId === null ? 'Create Tour Guide Schema' : 'Update Tour Guide Schema'}
              </LoadingButton>
            </Stack>
          </Card>
          <br />
          <br />
          <TourGuideConfigureSchemaListUI
            tableLoading={tableLoading}
            handlePageChange={handlePageChange}
            handleSizeChange={handleSizeChange}
            tableDataFromParent={tableData}
            handleEditRowParent={handleEditRow}
            handleEditRowEnable={handleEditRowEnable}
            setNameFromChild={setNameFromChild}
            loading={loading}
            setToolbarSchemaIdFromChild={setToolbarSchemaIdFromChild}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
}

TourGuideConfigureEditForm.propTypes = {
  currentUser: PropTypes.object,
  isEditMode: PropTypes.bool,
};
